// Training, models, auto-annotation, review resets and measurement exports (backend/app/api/autoannotate.py,
// annotations.py, export.py). Mirrors the matching methods of frontend/api_client.py.
import { api } from "./client";
import type { Project } from "./types";

export interface AnnotationStats {
  total: number; unannotated: number; in_progress: number; annotated: number; auto_annotated: number;
  reviewed_annotation_count: number; unreviewed_annotation_count: number; reviewed_image_count: number;
  /** Of the reviewed images, stored augmented copies (the Augment page). Absent from older servers. */
  augmented_reviewed_image_count?: number;
  /** Images a person marked empty; they train as background images and are counted in reviewed_image_count. Absent from older servers. */
  empty?: number;
  /** Of the empty images, generated background pictures (the Augment page): counted in `empty` and in the copies. Absent from older servers. */
  generated_background_count?: number;
  reviewed_with_corners_count: number; can_train: boolean; has_trained_model: boolean;
  /** The epochs a run gets when none are typed: sized for these images so it trains enough steps. Absent from older servers. */
  auto_epochs?: number;
  latest_model_version: number | null; latest_model_source: string | null;
  /** Reviewed annotations per class, copies included, and the original images they are on. Absent from older servers. */
  reviewed_by_class?: ClassCount[];
}
export interface ClassCount { class_id: number; name: string; annotations: number; original_images: number }
/**
 * Augmentation settings (insiso_core/augment.py AugmentConfig): probabilities 0-1, colour strength 0-2, zoom_out 0-0.6.
 * The Augment page's ranges: rotate_* and hue_* in degrees (-180 to 180), brightness_*, contrast_* and zoom_* from -1 to +1
 * (-100% to +100%); both ends 0 means off.
 */
export interface AugmentConfig {
  mosaic: number; fliplr: number; flipud: number; rotate90: number; colour: number; zoom_out: number; blur: number; noise: number;
  rotate_min: number; rotate_max: number; brightness_min: number; brightness_max: number;
  contrast_min: number; contrast_max: number; zoom_min: number; zoom_max: number; hue_min: number; hue_max: number;
}
export const augmentPresets = (pid: number) => api.get<Record<string, AugmentConfig>>(`/projects/${pid}/train/augment-presets`);
export interface AugmentPreview { original: string; samples: string[]; image_id: number; filename: string }
export const augmentPreview = (pid: number, body: { augmentation: AugmentConfig; image_id?: number | null; count?: number; img_size?: number }) =>
  api.post<AugmentPreview>(`/projects/${pid}/train/augment-preview`, { json: body });

/** The training image size suggested from the project's reviewed images (backend/app/ml/image_size.py). */
export interface ImageSizeSuggestion { suggested: number; reason: string; typical_size?: [number, number] }
export const suggestImageSize = (pid: number) => api.get<ImageSizeSuggestion>(`/projects/${pid}/train/image-size`);
export const annotationStats = (pid: number) => api.get<AnnotationStats>(`/projects/${pid}/annotation-stats`);

export type TrainDevice = "cpu" | "gpu";
export interface TrainParams {
  /** null: the server sizes the run for the data (its auto_epochs). */
  epochs: number | null; train_split: number; model_size: string; img_size: number; hailo_safe: boolean; corners: boolean; masks: boolean; early_stop: boolean; augmentation: AugmentConfig;
  device?: TrainDevice;
  /** Fine-tune from this version instead of the latest compatible one; from_scratch starts from none. */
  start_from?: number; from_scratch?: boolean;
  include_augmented?: boolean;
}
export interface TrainingJob {
  id: number; project_id: number; status: string; started_at: string | null; completed_at: string | null; log: string;
  created_at: string; queue_position: number | null; cancel_requested: boolean; device?: TrainDevice;
}
export const startTraining = (pid: number, p: Partial<TrainParams>) => api.post<TrainingJob>(`/projects/${pid}/train`, { json: p });
/** CPU or GPU for a run of this size: what the server offers and suggests (backend/app/ml/device_choice.py). */
export interface TrainOptions { gpu_available: boolean; cpu_fits: boolean; cpu_note: string | null; recommended: TrainDevice; reason: string }
export const trainOptions = (pid: number, modelSize: string, imgSize: number) =>
  api.get<TrainOptions>(`/projects/${pid}/train/options?model_size=${encodeURIComponent(modelSize)}&img_size=${imgSize}`);
/** Keep the "Train on" choice with the project (null: back to the suggestion); answers the project as it now is. */
export const setTrainDevice = (pid: number, device: TrainDevice | null) =>
  api.put<Project>(`/projects/${pid}/train/device`, { json: { device } });
export const cancelTraining = (pid: number) => api.post<TrainingJob>(`/projects/${pid}/train/cancel`);
export const trainingStatus = (pid: number) => api.get<TrainingJob>(`/projects/${pid}/train/status`);

export interface EpochRow { epoch: number; total_epochs: number; seconds: number; loss: number | null; lr: number | null; map50: number | null; map50_95: number | null; per_class_ap50?: Record<string, number> | null }
export interface TrainingMetrics { job_id: number; status: string; epochs: EpochRow[]; summary: { best_map50?: number; seconds_per_epoch?: number } }
export const trainingMetrics = (pid: number) => api.get<TrainingMetrics>(`/projects/${pid}/train/metrics`);

/** One row of GET /projects/{id}/models: a trained or imported version a person can download or export. */
export interface ModelVersionInfo {
  version_number: number; is_latest: boolean; created_at: string; trained_on_count: number;
  source: "trained" | "imported"; imported_from: string | null; classes: string[];
  model_size: string | null; img_size: number | null; epochs: number | null;
  corners: boolean; hailo_safe: boolean; masks: boolean; warm_started_from_version: number | null;
  size_bytes: number | null; available: boolean;
  /** What the run reached (null for versions saved before it was recorded, and imports): its last epoch and best mAP@0.5. */
  epochs_completed?: number | null; best_map50?: number | null;
  /** Marked Keep: its weights are never removed to save space. Absent from older servers. */
  keep?: boolean;
  /** The best-scoring version, whose weights are kept too. Absent from older servers. */
  best?: boolean;
  /** The confidence and NMS thresholds its validation chose, with the precision and recall they gave: what an export starts from. Null for versions recorded before this existed. */
  deploy?: RecommendedThresholds | null;
}
export interface RecommendedThresholds { conf: number; iou: number; precision: number; recall: number; epoch?: number; map50?: number }
export const listModels = (pid: number) => api.get<ModelVersionInfo[]>(`/projects/${pid}/models`);
/** Keep a version's weights for good, or let them go with the rest; answers the version's row as it now is. */
export const keepModel = (pid: number, version: number, keep: boolean) =>
  api.put<ModelVersionInfo>(`/projects/${pid}/models/${version}/keep`, { json: { keep } });

/** `version` omitted: the latest. */
export const downloadModel = (pid: number, version?: number | null) =>
  api.get<Blob>(`/projects/${pid}/model/download`, { as: "blob", query: { version } });

export interface OnnxOptions {
  target: "onnxruntime" | "tensorrt" | "hailo"; opset: number; include_nms: boolean; conf: number; iou: number; max_det: number;
  dynamic_batch: boolean; bundle: boolean; calib_limit: number;
  /** On: conf and iou are not sent, and the server uses the thresholds the version's validation measured (its deploy record), or the defaults for a version without them. */
  auto_thresholds: boolean;
}
export const exportOnnx = (pid: number, o: OnnxOptions, version?: number | null) => {
  // The server decides the thresholds unless numbers are sent, so with auto on they are left out of the request.
  const { auto_thresholds, conf, iou, ...rest } = o;
  return api.get<Blob>(`/projects/${pid}/model/export/onnx`, { as: "blob", query: { ...rest, ...(auto_thresholds ? {} : { conf, iou }), version } });
};

/** One compile of a Hailo bundle to a .hef on the server's compiler machine (backend/app/api/hailo_compile.py). */
export interface HailoCompileJob {
  id: number; project_id: number; model_version: number | null;
  status: "queued" | "sending" | "running" | "completed" | "failed" | "cancelled";
  stage: string; progress: number; hw_arch: string; filename: string; error: string; log_tail: string;
  /** Jobs ahead of this one, while it is queued. */
  queue_position: number | null; cancel_requested: boolean;
  /** Nothing is serving the queue: no compile client has been heard from. Absent from older servers. */
  stalled?: boolean;
  created_at: string | null; started_at: string | null; completed_at: string | null;
}
/** Whether this server has a compiler connected, and the project's latest compile. */
export const hailoCompileInfo = (pid: number) =>
  api.get<{ enabled: boolean; job: HailoCompileJob | null }>(`/projects/${pid}/model/compile/hailo`);
/** Exports the Hailo bundle on the server and queues it; 409 when the project already has a compile going. */
export const startHailoCompile = (pid: number, body: { version?: number | null; calib_limit?: number }) =>
  api.post<HailoCompileJob>(`/projects/${pid}/model/compile/hailo`, { json: body });
export const cancelHailoCompile = (pid: number, jobId: number) =>
  api.post<HailoCompileJob>(`/projects/${pid}/model/compile/hailo/${jobId}/cancel`);
/** The finished zip: the .hef with metadata.json, dgqp.npz and classes.txt. */
export const downloadHailoCompile = (pid: number, jobId: number) =>
  api.get<Blob>(`/projects/${pid}/model/compile/hailo/${jobId}/download`, { as: "blob" });

export interface ModelImportResult {
  version_number: number; classes?: string[]; model_size?: string; img_size?: number; corners?: boolean;
  classes_created?: string[]; classes_aliased?: Record<string, string>; warnings?: string[]; conflicts?: string[];
}
/** Throws ModelImportRefused: 409 with the missing classes, or 400 with the reasons the file cannot be used. */
export function importModel(pid: number, file: File, createClasses = false, classMapping?: Record<string, string>): Promise<ModelImportResult> {
  const form = new FormData();
  form.append("file", file, file.name);
  form.append("create_classes", String(createClasses));
  if (classMapping && Object.keys(classMapping).length) form.append("class_mapping", JSON.stringify(classMapping));
  return api.post<ModelImportResult>(`/projects/${pid}/model/import`, { form });
}

/** One thing a model found on a test picture: the shape auto-annotate would save (bbox, or polygon for corners/outlines). */
export interface TestDetection {
  class: string; class_id: number | null; confidence: number;
  shape_type: "bbox" | "polygon";
  coordinates: { x1: number; y1: number; x2: number; y2: number } | { points: [number, number][] };
}
/** A test picture's answer: its size as shown (EXIF turned) and what was found, most confident first; or why it could not be read. */
export interface TestResult { file: string; width: number; height: number; detections: TestDetection[]; error: string | null }
export interface ModelTestResponse { version: number; conf: number; results: TestResult[] }
/** Run a version (null: the latest) on pictures sent with the request; nothing is stored and no credits are charged. */
export function testModel(pid: number, files: File[], version: number | null = null): Promise<ModelTestResponse> {
  const form = new FormData();
  for (const f of files) form.append("files", f, f.name);
  if (version !== null) form.append("version", String(version));
  return api.post<ModelTestResponse>(`/projects/${pid}/model/test`, { form });
}

/** ``stopped``: why a batch ended before the last image (it keeps, and pays for, what it finished). */
export interface BatchResult { processed?: number; total_annotations?: number; remaining?: number; stopped?: "cancelled" | "credits" | null; message?: string }
/** `version` null annotates with the latest model version. */
export const autoAnnotateBatch = (pid: number, confidence: number, folder: string | null, version: number | null = null) =>
  api.post<BatchResult>(`/projects/${pid}/auto-annotate-batch`, { json: { confidence_threshold: confidence, ...(folder !== null ? { folder } : {}), ...(version !== null ? { version } : {}) } });

/** Stop the project's running batch after the images in progress. */
export const cancelAutoAnnotateBatch = (pid: number) => api.post<{ message: string }>(`/projects/${pid}/auto-annotate-batch/cancel`);

export const unreviewAnnotations = (pid: number) => api.post<{ cleared: number }>(`/projects/${pid}/annotations/unreview`);
export const clearUnreviewed = (pid: number) => api.post<{ annotations_deleted: number; images_affected: number }>(`/projects/${pid}/annotations/clear-unreviewed`);

export interface MeasurementExportOptions { scale: number; includeUnreviewed: boolean }
const measurementQuery = (o: MeasurementExportOptions) => ({ bias: 0, scale_mm_per_px: o.scale, ...(o.includeUnreviewed ? { include_unreviewed: "true" } : {}) });
export const exportMeasurementsCsv = (pid: number, o: MeasurementExportOptions) =>
  api.get<Blob>(`/projects/${pid}/export/measurements`, { as: "blob", query: measurementQuery(o) });
export const exportMeasurementsXlsx = (pid: number, o: MeasurementExportOptions) =>
  api.get<Blob>(`/projects/${pid}/export/measurements.xlsx`, { as: "blob", query: measurementQuery(o) });
